// types/sidebar-menu-item.ts
import { IconifyIcon } from "@iconify/react";
import { BadgeType } from "oc/components/sidebar/badge-registry";

export const PRODUCTS = {
  MYHR: "MyHR",
  MYCAFETERIA: "MyCafeteria",
  MYDOCUMENTS: "MyDocuments",
  MYLEAVE: "MyLeave",
  MYNEWS: "MyNews",
  MYTIME: "MyTime",
  MYTIMESHEET: "MyTimesheet",
} as const;

export const ROLE_CATEGORIES = {
  USER: "user",
  VIEWER: "viewer",
  EXAMINER: "examiner",
  ADMIN: "admin",
  EDITOR: "editor",
  UPLOADER: "uploader",
  ASSIGNER: "assigner",
} as const;

export const ROLES = {
  MYHR: {
    USER: `${PRODUCTS.MYHR}.${ROLE_CATEGORIES.USER}`,
    VIEWER: `${PRODUCTS.MYHR}.${ROLE_CATEGORIES.VIEWER}`,
    EXAMINER: `${PRODUCTS.MYHR}.${ROLE_CATEGORIES.EXAMINER}`,
    ADMIN: `${PRODUCTS.MYHR}.${ROLE_CATEGORIES.ADMIN}`,
  },
  MYCAFETERIA: {
    USER: `${PRODUCTS.MYCAFETERIA}.${ROLE_CATEGORIES.USER}`,
    ADMIN: `${PRODUCTS.MYCAFETERIA}.${ROLE_CATEGORIES.ADMIN}`,
  },
  MYDOCUMENTS: {
    VIEWER: `${PRODUCTS.MYDOCUMENTS}.${ROLE_CATEGORIES.VIEWER}`,
    UPLOADER: `${PRODUCTS.MYDOCUMENTS}.${ROLE_CATEGORIES.UPLOADER}`,
  },
  MYLEAVE: {
    USER: `${PRODUCTS.MYLEAVE}.${ROLE_CATEGORIES.USER}`,
    VIEWER: `${PRODUCTS.MYLEAVE}.${ROLE_CATEGORIES.VIEWER}`,
    EXAMINER: `${PRODUCTS.MYLEAVE}.${ROLE_CATEGORIES.EXAMINER}`,
    ADMIN: `${PRODUCTS.MYLEAVE}.${ROLE_CATEGORIES.ADMIN}`,
    ASSIGNER: `${PRODUCTS.MYLEAVE}.${ROLE_CATEGORIES.ASSIGNER}`,
  },
  MYNEWS: {
    USER: `${PRODUCTS.MYNEWS}.${ROLE_CATEGORIES.USER}`,
    EDITOR: `${PRODUCTS.MYNEWS}.${ROLE_CATEGORIES.EDITOR}`,
    ADMIN: `${PRODUCTS.MYNEWS}.${ROLE_CATEGORIES.ADMIN}`,
  },
  MYTIME: {
    USER: `${PRODUCTS.MYTIME}.${ROLE_CATEGORIES.USER}`,
    EXAMINER: `${PRODUCTS.MYTIME}.${ROLE_CATEGORIES.EXAMINER}`,
    ADMIN: `${PRODUCTS.MYTIME}.${ROLE_CATEGORIES.ADMIN}`,
  },
  MYTIMESHEET: {
    USER: `${PRODUCTS.MYTIMESHEET}.${ROLE_CATEGORIES.USER}`,
    EXAMINER: `${PRODUCTS.MYTIMESHEET}.${ROLE_CATEGORIES.EXAMINER}`,
    VIEWER: `${PRODUCTS.MYTIMESHEET}.${ROLE_CATEGORIES.VIEWER}`,
    ADMIN: `${PRODUCTS.MYTIMESHEET}.${ROLE_CATEGORIES.ADMIN}`,
  },
} as const;

export type RolesT =
  typeof ROLES extends Record<string, infer U>
    ? U extends Record<string, infer V>
      ? V
      : never
    : never;

export type ProductsT = (typeof PRODUCTS)[keyof typeof PRODUCTS];

export type RoleCategoriesT =
  (typeof ROLE_CATEGORIES)[keyof typeof ROLE_CATEGORIES];

export const LICENCES = {
  MYHR: "myHR",
  MYCAFETERIA: "myCafeteria",
  MYDOCUMENTS: "myDocuments",
  MYLEAVE: "myLeave",
  MYTIME: "myTime",
  MYTIMESHEET: "mySheet",
} as const;

export type LicencesT = (typeof LICENCES)[keyof typeof LICENCES];

interface SidebarMenuItemBase {
  /** Egyedi azonosító */
  id: string;
  /** Menü típus */
  type: "item" | "collapse" | "group" | "submenu" | "backMenu";
  /** Szöveges címke (i18n kulcs) */
  label: string;
  /** Fordítási kulcs (ha eltér a label értékétől) */
  translation?: string;
  /** Engedélyezett szerepkörök */
  roles?: RolesT[];
  /** Tiltott szerepkörök */
  notAllowedRoles?: RolesT[];
  /** Ha true, csak a szerepkörökön alapul a megjelenítés */
  rolesOnly?: boolean;
  /** Licence kulcs (pl. "myHR", "myTime" stb.) */
  licence?: LicencesT;
}

/** Item típus: útvonallal rendelkezik */
export interface SidebarItem extends SidebarMenuItemBase {
  type: "item";
  path: string;
  icon?: IconifyIcon;
  narrowMenuIcon?: IconifyIcon;
  badge?: BadgeType;
}

/** Collapse típus: gyerekekkel rendelkezik */
export interface SidebarCollapse extends SidebarMenuItemBase {
  type: "collapse";
  icon?: IconifyIcon;
  narrowMenuIcon?: IconifyIcon;
  badge?: BadgeType;
  path?: string;
  children: SidebarMenuItem[];
}

/** Csoport (címke nélküli, csak szekció) */
export interface SidebarGroup extends SidebarMenuItemBase {
  type: "group";
}

/** Almenü, nincs path, de lehet akkord-szerű viselkedése */
export interface SidebarSubmenu extends SidebarMenuItemBase {
  type: "submenu";
  icon?: IconifyIcon;
  narrowMenuIcon?: IconifyIcon;
}

/** Visszalépő menüpont, targetMenu-vel */
export interface SidebarBackMenu extends SidebarMenuItemBase {
  type: "backMenu";
  targetMenu: string;
  icon?: IconifyIcon;
  narrowMenuIcon?: IconifyIcon;
}

export type SidebarMenuItem =
  | SidebarItem
  | SidebarCollapse
  | SidebarGroup
  | SidebarSubmenu
  | SidebarBackMenu;
